/**
 * @fileoverview Per-product ad-spend breakdown: direct vs collection-allocated.
 * @module features/home/components/product-statistics/ProductAllocationBreakdown
 */
import { type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { CollectionAllocationDTO } from '@/lib/statisticsService'
import { formatMoney } from '@/shared/utils/money'
import { useReportingCurrency } from '@/features/settings/context/OrgSettingsProvider'

interface ProductAllocationBreakdownProps {
  productId: string
  directAdSpend: number
  /** Org-wide allocation dataset; may be undefined while the source query loads. */
  allocation?: CollectionAllocationDTO | undefined
}

export function ProductAllocationBreakdown({ productId, directAdSpend, allocation }: ProductAllocationBreakdownProps): ReactElement | null {
  const { t, i18n } = useTranslation('features-home')
  const { currency } = useReportingCurrency()
  const locale = i18n.language
  const sources = useMemo(() => {
    if (!allocation) return []
    return allocation.groups
      .map(g => {
        const member = g.members.find(m => m.productKey === productId)
        return member ? { path: g.canonicalPath, allocated: member.allocated, sharePercent: member.sharePercent } : null
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
  }, [allocation, productId])

  if (sources.length === 0) return null
  const allocatedTotal = sources.reduce((s, x) => s + x.allocated, 0)
  const total = directAdSpend + allocatedTotal

  return (
    <section className="rounded-xl border border-border/40 bg-card/30 px-6 py-6">
      <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {t('productStatistics.allocationBreakdown.title', 'Ad spend')}
      </h3>
      <p className="mb-4 text-xs text-muted-foreground">
        {t(
          'productStatistics.allocationBreakdown.subtitle',
          "How this product's advertising cost is composed"
        )}
      </p>
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">
            {t('productStatistics.allocationBreakdown.direct', 'Direct (own product URLs)')}
          </span>
          <span className="tabular-nums">{formatMoney(directAdSpend, currency ?? 'USD', locale)}</span>
        </div>
        {sources.map(s => (
          <div key={s.path} className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {t('productStatistics.allocationBreakdown.allocatedFrom', {
                path: s.path,
                percent: s.sharePercent.toFixed(0),
                defaultValue: 'Allocated from {{path}} · {{percent}}% share',
              })}
            </span>
            <span className="tabular-nums font-medium text-primary">+{formatMoney(s.allocated, currency ?? 'USD', locale)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-border/30 pt-2 font-semibold">
          <span>{t('productStatistics.allocationBreakdown.total', 'Total advertising')}</span>
          <span className="tabular-nums">{formatMoney(total, currency ?? 'USD', locale)}</span>
        </div>
      </div>
    </section>
  )
}
